import React, { useState, useEffect, useRef } from 'react';
import { motion, useAnimation, AnimatePresence, useInView } from 'framer-motion';
import { 
  ChevronDownIcon, 
  CursorClickIcon, 
  LightningBoltIcon, 
  ChartBarIcon, 
  FilterIcon,
  CashIcon, 
  LocationMarkerIcon,
  MenuIcon,
  XIcon,
  CheckCircleIcon,
  UserGroupIcon,
  CubeTransparentIcon,
  ShieldCheckIcon,
  BadgeCheckIcon,
  SparklesIcon,
  CurrencyDollarIcon,
  EyeIcon,
  PhotographIcon,
  ClockIcon,
  ArrowRightIcon,
  PlusIcon,
  ChevronRightIcon,
  SearchIcon,
  CalendarIcon,
  ChartPieIcon,
  CreditCardIcon,
  UserIcon,
  DeviceMobileIcon,
  GlobeAltIcon
} from '@heroicons/react/outline';

// Composant principal de la Landing Page
const MapCD = () => {
  const [scrollY, setScrollY] = useState(0);
    const [navbarOpen, setNavbarOpen] = useState(false);
    const [isNavTransparent, setIsNavTransparent] = useState(true);
    const [activeSlide, setActiveSlide] = useState(0);
    
    // Références pour l'animation au scroll
    const heroRef = useRef(null);
    const statsRef = useRef(null);
    const featuresRef = useRef(null);
    const platformRef = useRef(null);
    const screensRef = useRef(null);
    const partnersRef = useRef(null);
    const testimonialsRef = useRef(null);
    
    // InView Hooks pour déclencher les animations
    const isHeroInView = useInView(heroRef, { once: false });
    const isStatsInView = useInView(statsRef, { once: false });
    const isFeaturesInView = useInView(featuresRef, { once: false });
    const isPlatformInView = useInView(platformRef, { once: false });
    const isScreensInView = useInView(screensRef, { once: false });
    const isPartnersInView = useInView(partnersRef, { once: false });
    const isTestimonialsInView = useInView(testimonialsRef, { once: false });
    
    // Animations contrôlées
    const heroControls = useAnimation();
    const statsControls = useAnimation();
    const featuresControls = useAnimation();
    const platformControls = useAnimation();
    const screensControls = useAnimation();
    const partnersControls = useAnimation();
    const testimonialsControls = useAnimation();
    
    useEffect(() => {
      const handleScroll = () => {
        setScrollY(window.scrollY);
        setIsNavTransparent(window.scrollY < 50);
      };
      
      window.addEventListener('scroll', handleScroll);
      
      // Timer pour slideshow de la Hero Section
      const slideshowTimer = setInterval(() => {
        setActiveSlide((prev) => (prev + 1) % 3);
      }, 5000);
      
      return () => {
        window.removeEventListener('scroll', handleScroll);
        clearInterval(slideshowTimer);
      };
    }, []);
    
    // Effet pour les animations au scroll
    useEffect(() => {
      if (isHeroInView) heroControls.start("visible");
      if (isStatsInView) statsControls.start("visible");
      if (isFeaturesInView) featuresControls.start("visible");
      if (isPlatformInView) platformControls.start("visible");
      if (isScreensInView) screensControls.start("visible");
      if (isPartnersInView) partnersControls.start("visible");
      if (isTestimonialsInView) testimonialsControls.start("visible");
    }, [
      isHeroInView, isStatsInView, isFeaturesInView, isPlatformInView, 
      isScreensInView, isPartnersInView, isTestimonialsInView,
      heroControls, statsControls, featuresControls, platformControls, 
      screensControls, partnersControls, testimonialsControls
    ]);

  
    // Effets d'animation Framer Motion
    const fadeInUp = {
      hidden: { opacity: 0, y: 30 },
      visible: { 
        opacity: 1, 
        y: 0,
        transition: { 
          duration: 0.8,
          ease: "easeOut"
        }
      }
    };
    
    const staggerContainer = {
      hidden: { opacity: 0 },
      visible: {
        opacity: 1,
        transition: {
          staggerChildren: 0.1
        }
      }
    };
    
    const scaleIn = {
      hidden: { opacity: 0, scale: 0.8 },
      visible: { 
        opacity: 1, 
        scale: 1,
        transition: { duration: 0.6 }
      }
    };
    
    const slideInLeft = {
      hidden: { opacity: 0, x: -50 },
      visible: { 
        opacity: 1, 
        x: 0,
        transition: { duration: 0.7 }
      }
    };
    
    const slideInRight = {
      hidden: { opacity: 0, x: 50 },
      visible: { 
        opacity: 1, 
        x: 0,
        transition: { duration: 0.7 }
      }
    };

   return (
      <div className="min-h-screen bg-gray-50">

        <section 
                ref={screensRef}
                className="py-24 px-4 bg-gray-50 relative overflow-hidden" 
                id="écrans"
              >
                {/* Fond décoratif abstrait */}
                <div className="absolute inset-0 opacity-30 overflow-hidden">
                  <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
                    <defs>
                      <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" style={{ stopColor: '#E84E0E', stopOpacity: 0.1 }} />
                        <stop offset="100%" style={{ stopColor: '#E84E0E', stopOpacity: 0 }} />
                      </linearGradient>
                    </defs>
                    <path d="M0,0 L100,0 L100,100 C80,90 50,110 20,80 L0,100 Z" fill="url(#grad1)" />
                  </svg>
                </div>
                
                <div className="max-w-7xl mx-auto relative z-10">
                  <motion.div 
                    className="text-center mb-16"
                    initial="hidden"
                    animate={screensControls}
                    variants={staggerContainer}
                  >
                    <motion.div variants={scaleIn} className="inline-block bg-orange-100 text-[#E84E0E] text-sm font-semibold px-4 py-1 rounded-full mb-4">
                      NOTRE RÉSEAU
                    </motion.div>
                    <motion.h2 variants={fadeInUp} className="text-3xl md:text-5xl font-bold mt-2 mb-4 text-gray-900">
                      Découvrez notre ceinture digitale
                    </motion.h2>
                    <motion.p variants={fadeInUp} className="text-lg text-gray-600 max-w-3xl mx-auto">
                      Plus de 100 écrans stratégiquement placés dans les zones à fort trafic pour maximiser l'impact de vos campagnes
                    </motion.p>
                  </motion.div>
                  
                  {/* Carte interactive redesignée - Inspirée des UI cartographiques modernes */}
                  <motion.div 
                    className="mb-20"
                    initial="hidden"
                    animate={screensControls}
                    variants={fadeInUp}
                  >
                    <div className="bg-white rounded-2xl overflow-hidden shadow-xl border border-gray-200">
                      {/* Barre d'outils de la carte */}
                      <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex flex-col md:flex-row justify-between items-center">
                        <h3 className="text-xl font-bold text-gray-900 flex items-center mb-4 md:mb-0">
                          <LocationMarkerIcon className="h-5 w-5 mr-2 text-[#E84E0E]" />
                          Carte des emplacements
                        </h3>
                        
                        <div className="flex flex-wrap gap-2">
                          <div className="relative">
                            <select className="bg-white border border-gray-300 rounded-lg py-2 pl-4 pr-10 text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-[#E84E0E]">
                              <option>Tous les types</option>
                              <option>Grand format</option>
                              <option>Écrans LCD</option>
                              <option>Totems interactifs</option>
                            </select>
                            <ChevronDownIcon className="h-4 w-4 text-gray-500 absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" />
                          </div>
                          
                          <div className="relative">
                            <select className="bg-white border border-gray-300 rounded-lg py-2 pl-4 pr-10 text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-[#E84E0E]">
                              <option>Toutes les zones</option>
                              <option>Centre-ville</option>
                              <option>Centres commerciaux</option>
                              <option>Quartier des affaires</option>
                            </select>
                            <ChevronDownIcon className="h-4 w-4 text-gray-500 absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" />
                          </div>
                          
                          <div className="relative">
                            <select className="bg-white border border-gray-300 rounded-lg py-2 pl-4 pr-10 text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-[#E84E0E]">
                              <option>Tous les formats</option>
                              <option>Paysage</option>
                              <option>Portrait</option>
                              <option>Carré</option>
                            </select>
                            <ChevronDownIcon className="h-4 w-4 text-gray-500 absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" />
                          </div>
                          
                          <button className="bg-[#E84E0E] text-white text-sm py-2 px-4 rounded-lg flex items-center">
                            <FilterIcon className="h-4 w-4 mr-1" />
                            <span>Filtrer</span>
                          </button>
                        </div>
                      </div>
                      
                      {/* Carte principale */}
                      <div className="relative h-[500px] md:h-[600px] bg-gray-100 overflow-hidden">
                        {/* Image de carte en fond */}
                        <img 
                          src="https://images.unsplash.com/photo-1526778548025-fa2f459cd5ce?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80" 
                          alt="Carte de la ville" 
                          className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 hover:scale-105"
                        />
                        
                        {/* Overlay sur la carte */}
                        <div className="absolute inset-0 bg-gray-900 opacity-40"></div>
                        
                        {/* Emplacements des écrans - Design inspiré des UI de mapping sur Dribbble */}
                        {[...Array(12)].map((_, index) => {
                          const positions = [
                            { left: '20%', top: '30%', name: 'Centre Commercial Élite', type: 'Grand écran LED', visitors: '15K/jour' },
                            { left: '50%', top: '20%', name: 'Place du Marché Central', type: 'Écran LED extérieur', visitors: '12K/jour' },
                            { left: '80%', top: '40%', name: 'Gare Centrale', type: 'Totem interactif', visitors: '22K/jour' },
                            { left: '35%', top: '60%', name: 'Avenue Principale', type: 'Mur digital', visitors: '18K/jour' },
                            { left: '70%', top: '70%', name: 'Galerie Marchande', type: 'Écran LCD HD', visitors: '9K/jour' },
                            { left: '15%', top: '75%', name: 'Boulevard Sud', type: 'Écran LED extérieur', visitors: '7K/jour' },
                            { left: '60%', top: '50%', name: 'Zone Piétonne', type: 'Totem interactif', visitors: '20K/jour' },
                            { left: '90%', top: '80%', name: 'Terminal Aéroport', type: 'Écran LED', visitors: '25K/jour' },
                            { left: '40%', top: '30%', name: 'Quartier des Affaires', type: 'Écran géant', visitors: '17K/jour' },
                            { left: '75%', top: '25%', name: 'Esplanade Culturelle', type: 'Mur LED', visitors: '8K/jour' },
                            { left: '25%', top: '15%', name: 'Centre Sportif', type: 'Écran extérieur', visitors: '14K/jour' },
                            { left: '65%', top: '85%', name: 'Parc Commercial', type: 'Grand format LED', visitors: '11K/jour' },
                          ];
                          
                          return (
                            <motion.div
                              key={index}
                              className="absolute cursor-pointer group z-20"
                              style={positions[index]}
                              initial={{ scale: 0 }}
                              whileInView={{ 
                                scale: [0, 1.2, 1],
                              }}
                              transition={{ 
                                delay: index * 0.1,
                                duration: 0.8
                              }}
                            >
                              <div className="relative">
                                {/* Cercle pulsant autour du marqueur */}
                                <div className="absolute inset-0 -m-1 rounded-full bg-[#E84E0E] opacity-30 group-hover:opacity-40 animate-ping" style={{ animationDuration: '3s' }}></div>
                                
                                {/* Marqueur principal */}
                                <div className="w-8 h-8 bg-gradient-to-r from-[#E84E0E] to-orange-600 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-200 border-2 border-white">
                                  <div className="w-2 h-2 bg-white rounded-full"></div>
                                </div>
                                
                                {/* Info-bulle de localisation */}
                                <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-3 bg-white rounded-xl p-3 shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none w-48 border border-gray-200 z-30">
                                  <div className="font-bold text-gray-900 mb-1">{positions[index].name}</div>
                                  <div className="text-sm text-gray-500 mb-2">{positions[index].type}</div>
                                  <div className="flex items-center text-xs text-gray-700">
                                    <EyeIcon className="h-3 w-3 mr-1 text-[#E84E0E]" />
                                    <span>{positions[index].visitors}</span>
                                  </div>
                                  
                                  {/* Flèche pointant vers le marqueur */}
                                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 transform rotate-45 w-3 h-3 bg-white border-r border-b border-gray-200"></div>
                                </div>
                              </div>
                            </motion.div>
                          );
                        })}
                        
                        {/* Contrôles de la carte en overlay */}
                        <div className="absolute bottom-6 left-6 right-6 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
                          <div className="bg-white rounded-xl shadow-xl p-4 w-full md:w-auto">
                            <div className="flex items-center space-x-2 mb-3">
                              <div className="h-3 w-3 rounded-full bg-[#E84E0E]"></div>
                              <span className="text-sm font-medium text-gray-900">100+ écrans disponibles</span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <button className="bg-[#E84E0E] text-white text-xs py-1 px-3 rounded-full">Tous</button>
                              <button className="bg-gray-100 text-gray-700 text-xs py-1 px-3 rounded-full hover:bg-gray-200">Centre-ville</button>
                              <button className="bg-gray-100 text-gray-700 text-xs py-1 px-3 rounded-full hover:bg-gray-200">Commerces</button>
                              <button className="bg-gray-100 text-gray-700 text-xs py-1 px-3 rounded-full hover:bg-gray-200">Transports</button>
                            </div>
                          </div>
                          
                          <motion.button 
                            className="bg-gradient-to-r from-[#E84E0E] to-orange-600 text-white font-bold py-3 px-6 rounded-xl shadow-lg flex items-center space-x-2 w-full md:w-auto justify-center"
                            whileHover={{ scale: 1.05, boxShadow: "0 15px 30px -5px rgba(232, 78, 14, 0.4)" }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <span>Explorer tous les emplacements</span>
                            <ArrowRightIcon className="h-5 w-5" />
                          </motion.button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                  
                  {/* Catégories d'écrans - Design inspiré des cards modernes de Dribbble */}
                  <motion.div 
                    className="mb-20"
                    initial="hidden"
                    animate={screensControls}
                    variants={staggerContainer}
                  >
                    <motion.h3 
                      variants={fadeInUp}
                      className="text-2xl md:text-3xl font-bold mb-10 text-center text-gray-900"
                    >
                      Types d'écrans disponibles
                    </motion.h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                      {[
                        {
                          image: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
                          title: "Écrans LED extérieurs",
                          description: "Idéals pour une visibilité maximale dans les zones urbaines à fort trafic",
                          specs: "Format jusqu'à 8x5m, 4K UHD",
                          features: ["Haute luminosité", "Résistant aux intempéries", "Visibilité jusqu'à 300m"]
                        },
                        {
                          image: "https://images.unsplash.com/photo-1581091226033-d5c48150dbaa?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
                          title: "Écrans LCD indoor",
                          description: "Parfaits pour les centres commerciaux et espaces intérieurs",
                          specs: "Format de 55\" à 85\", Full HD",
                          features: ["Haute définition", "Son intégré disponible", "Interactivité optionnelle"]
                        },
                        {
                          image: "https://images.unsplash.com/photo-1519951468518-33c8bee8ff61?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
                          title: "Totems interactifs",
                          description: "Engagez votre audience avec des expériences tactiles immersives",
                          specs: "Format 2x3m, écran tactile",
                          features: ["Multi-touch", "Connexion Internet", "Analytics avancés"]
                        }
                      ].map((screen, index) => (
                        <motion.div 
                          key={index}
                          className="bg-white rounded-2xl overflow-hidden shadow-xl group relative"
                          variants={scaleIn}
                          transition={{ delay: index * 0.2 }}
                          whileHover={{ 
                            y: -8,
                            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)"
                          }}
                        >
                          <div className="h-52 md:h-64 overflow-hidden relative">
                            <img 
                              src={screen.image}
                              alt={screen.title}
                              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-60"></div>
                            <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                              <h4 className="font-bold text-lg">{screen.title}</h4>
                              <p className="text-white text-opacity-80 text-sm">{screen.specs}</p>
                            </div>
                          </div>
                          
                          <div className="p-6">
                            <p className="text-gray-600 mb-4">{screen.description}</p>
                            <div className="space-y-2 mb-6">
                              {screen.features.map((feature, i) => (
                                <div key={i} className="flex items-center">
                                  <CheckCircleIcon className="h-5 w-5 mr-2 text-green-500" />
                                  <span className="text-gray-700 text-sm">{feature}</span>
                                </div>
                              ))}
                            </div>
                            <button className="text-[#E84E0E] font-medium flex items-center text-sm hover:underline">
                              En savoir plus
                              <ArrowRightIcon className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                            </button>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                  
                  {/* Avantages en deux colonnes - Design inspiré des dashboards sur Dribbble */}
                  <motion.div 
                    className="grid grid-cols-1 md:grid-cols-2 gap-8"
                    initial="hidden"
                    animate={screensControls}
                    variants={staggerContainer}
                  >
                    <motion.div
                      className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 relative overflow-hidden"
                      variants={slideInLeft}
                      transition={{ delay: 0.1 }}
                      whileHover={{ y: -5 }}
                    >
                      <div className="absolute -right-20 -top-20 w-40 h-40 bg-orange-50 rounded-full transform -rotate-12"></div>
                      
                      <div className="relative">
                        <div className="flex items-start mb-6">
                          <div className="mr-4 p-3 rounded-xl shadow-lg bg-gradient-to-br from-[#E84E0E] to-orange-600">
                            <CashIcon className="h-8 w-8 text-white" />
                          </div>
                          <div>
                            <h3 className="text-xl font-bold mb-2 text-gray-900">Tarification dynamique</h3>
                            <p className="text-gray-600">Des prix adaptés à tous les budgets grâce à notre système intelligent qui s'ajuste selon la demande et la saisonnalité</p>
                          </div>
                        </div>
                        
                        <div className="space-y-4 mb-8">
                          {["Paiement à l'usage", "Pas d'engagement minimum", "Optimisation automatique du budget"].map((item, i) => (
                            <div key={i} className="flex items-center">
                              <div className="h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mr-3">
                                <CheckCircleIcon className="h-4 w-4 text-green-600" />
                              </div>
                              <span className="text-gray-700">{item}</span>
                            </div>
                          ))}
                        </div>
                        
                        <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                          <div className="flex justify-between items-center mb-3">
                            <span className="text-gray-700 font-medium">Tarif moyen par écran</span>
                            <div className="flex space-x-1">
                              <span className="text-xs py-1 px-2 bg-green-100 text-green-800 rounded-full">-15% vs marché</span>
                            </div>
                          </div>
                          <div className="flex items-end space-x-2 mb-4">
                            <span className="text-3xl font-bold text-gray-900">2500</span>
                            <span className="text-gray-500 pb-1">FCFA/heure</span>
                          </div>
                          <button className="w-full py-3 px-4 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-800 font-medium rounded-xl hover:from-gray-200 hover:to-gray-300 transition-colors flex items-center justify-center">
                            <span>Voir la grille tarifaire</span>
                            <ChevronRightIcon className="h-4 w-4 ml-1" />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                    
                    <motion.div
                      className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 relative overflow-hidden"
                      variants={slideInRight}
                      transition={{ delay: 0.3 }}
                      whileHover={{ y: -5 }}
                    >
                      <div className="absolute -left-20 -bottom-20 w-40 h-40 bg-blue-50 rounded-full transform -rotate-12"></div>
                      
                      <div className="relative">
                        <div className="flex items-start mb-6">
                          <div className="mr-4 p-3 rounded-xl shadow-lg bg-gradient-to-br from-blue-500 to-blue-600">
                            <ChartBarIcon className="h-8 w-8 text-white" />
                          </div>
                          <div>
                            <h3 className="text-xl font-bold mb-2 text-gray-900">Analytics avancés</h3>
                            <p className="text-gray-600">Mesurez l'impact de vos campagnes avec des données précises sur l'audience touchée et l'engagement généré</p>
                          </div>
                        </div>
                        
                        <div className="space-y-4 mb-6">
                          {["Rapports en temps réel", "Estimation d'audience", "Segmentation démographique", "Export de données"].map((item, i) => (
                            <div key={i} className="flex items-center">
                              <div className="h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                                <CheckCircleIcon className="h-4 w-4 text-blue-600" />
                              </div>
                              <span className="text-gray-700">{item}</span>
                            </div>
                          ))}
                        </div>
                        
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                          <div className="h-32 relative">
                            <div className="absolute inset-0 flex items-end justify-around">
                              {[60, 75, 45, 85, 65, 90, 70].map((height, i) => (
                                <div key={i} className="h-full flex-1 flex flex-col justify-end px-0.5">
                                  <motion.div
                                    className="bg-gradient-to-t from-blue-500 to-[#E84E0E] rounded-t-sm"
                                    initial={{ height: 0 }}
                                    whileInView={{ height: `${height}%` }}
                                    transition={{ duration: 1, delay: i * 0.1 }}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  </motion.div>
                </div>
              </section>
        {/* Autres sections seront ajoutées ici */}
      </div>
    );
  };
  
  export default MapCD;